/**
 * Candidate · interview MIC GATE — the pure decision layer behind the voice interview.
 *
 * Everything here is a plain function of plain values so it can be reasoned about and pinned by
 * tests, because the bugs it exists to kill were all "the component decided one thing while the
 * browser was doing another":
 *
 *  - Asha's own question appearing inside "Your spoken answer" (the mic was live while
 *    `speechSynthesis` was talking, and the recogniser happily transcribed the speakers);
 *  - the mic flickering on and off many times a second (an effect that read `listening` AND
 *    called `start()`/`stop()`, so every start re-triggered the effect that caused it);
 *  - the mic being dead after the tail guard because nothing restarted the recogniser once
 *    Chrome ended it on its own.
 *
 * The component's job is to compute the INPUTS honestly; `shouldListen` alone decides. Nothing in
 * this file touches the DOM, React, or the Web Speech API — the interview stays 100% client-side
 * and we still pay nothing for speech.
 */

/**
 * How long the mic stays shut AFTER Asha stops speaking.
 *
 * There is no echo cancellation for synthesised speech: the recogniser hears the tail of the
 * utterance coming back through the laptop speakers a beat after `onend` fires, and transcribes
 * it into the candidate's answer. ~450ms covers that tail without a silence the candidate reads
 * as "it's broken".
 */
export const TTS_TAIL_GUARD_MS = 450;

/**
 * The Azure-engine tail guard. Azure TTS renders as PAGE audio (SpeakerAudioDestination), which
 * the browser's echo canceller can see and subtract — so the guard only needs to cover AEC
 * convergence, not a whole uncancellable echo tail. 150ms is inaudible as a pause.
 */
export const AZURE_TAIL_GUARD_MS = 150;

/** First retry delay after the recogniser ends unexpectedly. */
export const RESTART_BASE_MS = 250;
/** Ceiling for the exponential backoff, so a broken mic costs one retry every few seconds. */
export const RESTART_MAX_MS = 4_000;
/** A recognition session this short with nothing heard is a failure, not a real pause. */
export const PRODUCTIVE_SESSION_MS = 1_500;
/** After this many consecutive failed sessions the mic is treated as unusable. */
export const MAX_CONSECUTIVE_FAILURES = 6;

export type AnswerMode = "voice" | "type";

export interface ListenInput {
  /** The browser exposes SpeechRecognition at all. */
  supported: boolean;
  /** The mic is denied / has no device / the recogniser refused to stay up — offer typing. */
  micBlocked: boolean;
  /** Tab is in the background. Chrome starves recognition there; restarting is a retry storm. */
  pageHidden: boolean;
  mode: AnswerMode;
  hasQuestion: boolean;
  /** A code question with an exercise renders the editor, not the voice surface. */
  usesCodeEditor: boolean;
  /** Asha is talking (or about to — the flag is raised BEFORE speak()). */
  speaking: boolean;
  /** An answer is in flight; anything said now belongs to no question. */
  pending: boolean;
  /** Epoch ms until which the mic stays shut (TTS tail guard). */
  guardUntil: number;
  now: number;
}

/**
 * Should the microphone be live right this instant?
 *
 * The single source of truth for mic state. Deliberately expressed as INTENT — it says nothing
 * about whether a recogniser happens to be running, so the effect that applies it can never
 * depend on the state it mutates.
 */
export function shouldListen(i: ListenInput): boolean {
  if (!i.supported || i.micBlocked || i.pageHidden) return false;
  if (!i.hasQuestion || i.usesCodeEditor) return false;
  if (i.mode !== "voice") return false;
  // Rule zero: never, under any circumstance, listen while Asha is speaking.
  if (i.speaking) return false;
  if (i.pending) return false;
  return i.now >= i.guardUntil;
}

export interface ResultGateInput {
  speaking: boolean;
  guardUntil: number;
  /** When the recognition result arrived (epoch ms). */
  at: number;
}

/**
 * Belt and braces for `onresult`: even if a recogniser is somehow still live, a transcript
 * produced while Asha was speaking (or inside the tail guard) is HER voice, not the candidate's,
 * and must never reach the answer. The candidate cannot undo text they never said.
 */
export function shouldAcceptResult(i: ResultGateInput): boolean {
  if (i.speaking) return false;
  return i.at >= i.guardUntil;
}

/** The guard window opened by Asha finishing an utterance. */
export function guardAfterSpeech(endedAt: number, guardMs: number = TTS_TAIL_GUARD_MS): number {
  return endedAt + guardMs;
}

/** Exponential backoff for restarting a recogniser that keeps dying — never a tight loop. */
export function restartDelayMs(consecutiveFailures: number): number {
  if (consecutiveFailures <= 0) return RESTART_BASE_MS;
  return Math.min(RESTART_MAX_MS, RESTART_BASE_MS * 2 ** consecutiveFailures);
}

/**
 * Chrome ends recognition by itself after a stretch of silence — that is normal and must NOT
 * count against the retry budget, or a thoughtful candidate who pauses gets their mic switched
 * off. Only a session that produced nothing AND died immediately is a real failure.
 */
export function nextFailureCount(
  previous: number,
  session: { gotResult: boolean; sessionMs: number },
): number {
  if (session.gotResult || session.sessionMs >= PRODUCTIVE_SESSION_MS) return 0;
  return previous + 1;
}

/** Stop retrying and move the candidate to typing rather than let them talk into a dead mic. */
export function shouldGiveUp(consecutiveFailures: number): boolean {
  return consecutiveFailures >= MAX_CONSECUTIVE_FAILURES;
}

/**
 * SpeechRecognition error codes that mean "this mic is not coming back" — retrying them is
 * pointless and, for `not-allowed`, re-prompts the candidate forever.
 * `no-speech`, `aborted` and `network` are transient and deliberately absent.
 */
export function isFatalRecognitionError(code: string | undefined): boolean {
  return code === "not-allowed" || code === "service-not-allowed";
}

export interface RecognitionEndInput {
  /** Do we still want the mic? (i.e. it is the candidate's turn) */
  intent: boolean;
  previousFailures: number;
  /** Did this session transcribe anything at all? */
  gotResult: boolean;
  /** How long the session lived before the browser ended it. */
  sessionMs: number;
  pageHidden: boolean;
}
export interface RecognitionEndPlan {
  /** The updated consecutive-failure count to carry forward. */
  failures: number;
  /** restart: put the mic back · idle: leave it off · give-up: the mic is unusable, offer typing. */
  action: "restart" | "idle" | "give-up";
  /** Only meaningful for "restart". */
  delayMs: number;
}

/**
 * What to do when the recogniser ends — the whole "not capturing" bug in one function.
 *
 * Chrome ends recognition on its own after silence, so SOMETHING has to put it back or the
 * candidate spends the rest of the question talking into a dead mic. But a blind restart is a
 * spin loop when the mic is denied or the tab is hidden. So: restart while we still want the mic,
 * back off when sessions keep dying instantly, and eventually give up and offer a keyboard.
 */
export function planAfterRecognitionEnd(i: RecognitionEndInput): RecognitionEndPlan {
  const failures = nextFailureCount(i.previousFailures, { gotResult: i.gotResult, sessionMs: i.sessionMs });
  if (shouldGiveUp(failures)) return { failures, action: "give-up", delayMs: 0 };
  // A hidden tab starves recognition; restarting into one is a guaranteed retry storm. The intent
  // is re-applied when the candidate comes back, so nothing is lost by waiting.
  if (!i.intent || i.pageHidden) return { failures, action: "idle", delayMs: 0 };
  return { failures, action: "restart", delayMs: restartDelayMs(failures) };
}
